import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from 'react';
import {
  PerformanceFormComponent,
  APIEvaluationForm,
  Question,
  EvaluationFormPayload,
  ComponentSettingsPayload,
} from '../types';
import { useAppSelector } from '@common/hooks';
import { selected } from '@modules/organisation/selectors/organisation';
import {
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from 'react-router';
import { AlertService } from '@common/services/alert';
import { useTranslation } from 'react-i18next';
import { fetchPerformanceForm } from '../action/fetch-performance-form';
import { createPerformanceForm } from '../action/create-performance-form';
import { updatePerformanceForm } from '../action/update-performance-form';
import { createQuestion } from '../action/create-question';
import { updateComponent } from '../action/upadate-question';
import { saveScreenOrder } from '../action/save-component-order';
import { deleteComponent } from '../action/delete-component';
import { duplicateComponent } from '../action/dublicate-component';
import { useRequest } from '@common/services/api';

interface PerformanceStore {
  form: APIEvaluationForm;
  competency: any[];
  competencyData: any[];
  loading: boolean;
  setForm: Dispatch<SetStateAction<APIEvaluationForm>>;
  components: PerformanceFormComponent[];
  setComponents: Dispatch<SetStateAction<PerformanceFormComponent[]>>;
  drafts: Record<string, any>;
  updateDraft: (componentId: string, partial: Record<string, any>) => void;
  clearDrafts: () => void;
  lastSavedOrder: string[] | null;
  handleCreatePerformanceForm: (
    form: EvaluationFormPayload
  ) => Promise<APIEvaluationForm>;
  handleUpdatePerformanceForm: (
    id: string,
    payload: Partial<EvaluationFormPayload>
  ) => Promise<APIEvaluationForm>;
  handleUpdateScreenOrder: (formId: string, order: string[]) => void;
  handleSaveScreenOrder: (
    formId: string,
    order: string[]
  ) => Promise<void>;
  handleCreateQuestion: (
    formId: string,
    payload: Question
  ) => Promise<{ data: APIEvaluationForm }>;
  handleUpdateComponent: (
    formId: string,
    componentId: string,
    payload: ComponentSettingsPayload | any,
    options?: { silent?: boolean }
  ) => Promise<{ data: PerformanceFormComponent }>;
  handleDeleteQuestion: (formId: string, componentId: string) => Promise<void>;
  handleDuplicateQuestion: (
    formId: string,
    componentId: string
  ) => Promise<string | undefined>;
  categories: any[];
}

const PerformanceContext = createContext<PerformanceStore | null>(null);

export const PerformanceStoreProvider = ({
  children,
}: PropsWithChildren<{ basePath: string }>) => {
  const { t } = useTranslation();
  const organisation = useAppSelector(selected);
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { search } = useLocation();
  const match = useRouteMatch();
  const { networkId } = match.params as { networkId: string };
  const basePath = networkId ? `/networks/${networkId}` : '/admin';
  const [form, setForm] = useState<APIEvaluationForm>();
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, any>>({});

  useEffect(() => {
    if (id) {
      handleFetchPerformanceForm(id);
    }
  }, [id]);

  const {
    data: competencyData,
  } = useRequest(
    `/v1/organisations/${organisation.id}/performance/competencies`
  );

  const handleFetchPerformanceForm = useCallback(
    async (formId: string) => {
      setLoading(true);
      const { data } = await fetchPerformanceForm(organisation.id, formId);
      setForm(data);
      // Reset drafts when fetching a fresh form to avoid stale edits from another form
      setDrafts({});
      setLoading(false);
      return data;
    },
    [organisation.id]
  );

  const { data: categories } = useRequest(
    `/v1/organisations/${organisation.id}/performance/competency/categories`
  );

  const handleCreatePerformanceForm = useCallback(
    async (formData: EvaluationFormPayload) => {
      try {
        const { data } = await createPerformanceForm(organisation.id, formData);
        setForm(data);
        history.replace(
          `${basePath}/performance/forms/${data.id}/edit/components${search}`
        );
        AlertService.success(
          t('performance:form_created', { title: data.title })
        );
      } catch (response) {
        // @ts-expect-error
        return AlertService.forStatus(response.status_code, {
          warning: t('performance:form_create_warning'),
          error: t('performance:form_create_error'),
        });
      }
    },
    [organisation.id]
  );

  const handleUpdatePerformanceForm = useCallback(
    async (
      formId: string,
      payload: EvaluationFormPayload,
    ) => {
      try {
        const { data } = await updatePerformanceForm(
          organisation.id,
          formId,
          payload
        );
        setForm(data);
        return { data };
      } catch (response) {
        // @ts-expect-error
        AlertService.forStatus(response.status_code, {
          warning: t('performance:form_update_warning'),
          error: t('performance:form_update_error'),
        });
      }
    },
    [organisation.id]
  );


  const handleCreateQuestion = useCallback(
    async (formId: string, payload: Question) => {
      try {
        const payloadData = {
          type: payload.type,
        };
        const { data } = await createQuestion(
          organisation.id,
          formId,
          payloadData
        );
        setForm(
          (prev) => prev && {
              ...prev,
              components: [...prev.components, data.components[data.components.length - 1]],
            }
        );

        AlertService.success(t('performance:question_created_success'));
        return { data };
      } catch (response) {
        // @ts-expect-error
        AlertService.forStatus(response.status_code, {
          warning: t('performance:question_created_warning'),
          error: t('performance:question_created_error'),
        });
      }
    },
    [organisation.id, basePath, form, history, t]
  );

  const handleUpdateComponent = useCallback(
    async (
      formId: string,
      componentId: string,
      payload: ComponentSettingsPayload
    ) => {
      try {
        const result = await updateComponent(
          organisation.id,
          formId,
          componentId,
          payload
        );
        setForm((prev) => {
          if (!prev) return prev as any;
          const incoming = result.data;
          const replaced = (prev.components || []).map((c) => (c.id === componentId ? incoming : c)
          );
          const exists = replaced.some((c) => c.id === incoming.id);
          const hasOld = replaced.some((c) => c.id === componentId);
          const components = exists ? replaced : hasOld ? replaced : [...replaced, incoming];
          return { ...prev, components } as any;
        });
        if ((result.data as any)?.id && result.data.id !== componentId) {
          setDrafts((prev) => {
            if (!prev || !prev[componentId]) return prev;
            const next = { ...prev } as Record<string, any>;
            next[result.data.id] = next[componentId];
            delete next[componentId];
            return next;
          });
        }
        return { data: result.data };
      } catch (err: any) {
        AlertService.forStatus(err.status_code, {
          warning: t('performance:component_update_warning'),
          error: t('performance:component_update_error'),
        });
      }
    },
    [organisation.id]
  );

  const handleUpdateScreenOrder = useCallback(
    (formId: string, order: string[]) => {
      setForm((prev) => {
        if (!prev) return prev;

        const reorderedComponents = order
          .map((componentId, index) => {
            const component = prev.components.find((comp) => comp.id === componentId);
            return component ? { ...component, index } : null;
          })
          .filter(Boolean) as PerformanceFormComponent[];

        return {
          ...prev,
          components: reorderedComponents,
        };
      });
    },
    []
  );

  const handleSaveScreenOrder = useCallback(
    async (
      formId: string,
      order: string[],
    ): Promise<void> => {
      try {
        await saveScreenOrder(organisation.id, formId, order);
          AlertService.success(t('performance:form_order_updated'));
      } catch (e: any) {
        if (e && typeof e.status_code === 'number') {
          AlertService.forStatus(e.status_code, {
            warning: t('performance:form_order_updated_warning'),
            error: t('performance:form_order_updated_error'),
          });
        }
      }
    },
    [organisation.id]
  );

  const handleDeleteQuestion = useCallback(
    async (formId: string, componentId: string) => {
      try {
        await deleteComponent(organisation.id, formId, componentId);
        setForm((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            components: prev.components.filter(
              (comp) => comp.id !== componentId
            ),
          };
        });
        AlertService.success(t('performance:question_deleted_success'));
      } catch (e: any) {
        AlertService.forStatus(e.status_code, {
          warning: t('performance:form_order_updated_warning'),
          error: t('performance:form_order_updated_error'),
        });
      }
    },
    [organisation.id]
  );

  const handleDuplicateQuestion = useCallback(
    async (formId: string, componentId: string) => {
      try {
        const { data } = await duplicateComponent(
          organisation.id,
          formId,
          componentId
        );

        setForm(data as any);

        AlertService.success(t('performance:question_duplicated_success'));
        return data;
      } catch (e: any) {
        AlertService.forStatus(e.status_code, {
          warning: t('performance:form_order_updated_warning'),
          error: t('performance:form_order_updated_error'),
        });
      }
    },
    [organisation.id]
  );

  const contextValue = useMemo(
    () => ({
      form,
      loading,
      categories,
      competencyData,
      setForm,
      drafts,
      updateDraft: (componentId: string, partial: Record<string, any>) => {
        setDrafts((prev) => ({
          ...prev,
          [componentId]: {
            ...(prev[componentId] || {}),
            ...partial,
          },
        }));
      },
      clearDrafts: () => setDrafts({}),
      handleCreatePerformanceForm,
      handleUpdatePerformanceForm,
      handleUpdateScreenOrder,
      handleSaveScreenOrder,
      handleCreateQuestion,
      handleUpdateComponent,
      handleDeleteQuestion,
      handleDuplicateQuestion,
    }),
    [
      form,
      loading,
      categories,
      competencyData,
      setForm,
      drafts,
      handleCreatePerformanceForm,
      handleUpdatePerformanceForm,
      handleUpdateScreenOrder,
      handleSaveScreenOrder,
      handleCreateQuestion,
      handleUpdateComponent,
      handleDeleteQuestion,
      handleDuplicateQuestion,
    ]
  );

  return (
    <PerformanceContext.Provider
      value={contextValue as unknown as PerformanceStore}
    >
      {children}
    </PerformanceContext.Provider>
  );
};

export const usePerformanceStore = (): PerformanceStore => {
  const context = useContext(PerformanceContext);
  if (!context) {
    throw new Error(
      'usePerformanceStore must be used within a PerformanceStoreProvider'
    );
  }
  return context;
};

export const withPerformanceStore =
  (Component: React.ComponentType<any>) => (props: any) => (
    <PerformanceStoreProvider basePath={props.basePath}>
      <Component {...props} />
    </PerformanceStoreProvider>
    );
